/**
 * `connectivity` — the app's single source of truth for "is our backend reachable".
 *
 * Local-first means the backend can be a loopback process (no internet needed) OR the cloud
 * API, so reachability is NOT `navigator.onLine` — that reports the OS network interface, not
 * whether OUR backend answers (a desktop loopback build is reachable with the laptop's WiFi
 * off; a cloud build is unreachable the instant the WAN drops even though `onLine` stays
 * true). Reachability is therefore derived from ACTUAL fetch outcomes against the backend,
 * reported by the REST client: a response (any status) marks us reachable; a thrown fetch
 * (transport error) marks us unreachable.
 *
 * On change we drive `@tanstack/query`'s `onlineManager`. When the backend is unreachable,
 * every query PAUSES — it serves the persisted IndexedDB cache (or stays in its loading
 * state) instead of firing and failing in a storm. That storm, multiplied across every
 * in-flight query at the moment a connection drops, is the "UI explosion" we're killing. When
 * we become reachable again, paused queries auto-resume and revalidate.
 *
 * While unreachable a lightweight prober pings the backend on an interval (bypassing the
 * paused query layer) so we notice the moment it returns. `window` online/offline/focus events
 * only *trigger a probe* — they never set state directly, precisely because the loopback
 * desktop build can lose WiFi while its local backend stays perfectly reachable.
 */

import { onlineManager } from '@tanstack/react-query';

type Probe = () => Promise<boolean>;

/** How often to ping the backend while we believe it's unreachable. */
const PROBE_INTERVAL_MS = 5_000;

let reachable = true;
let probe: Probe | null = null;
let probeTimer: ReturnType<typeof setInterval> | null = null;
let probing = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function startProber(): void {
  if (probeTimer !== null || probe === null) return;
  probeTimer = setInterval(() => void runProbe(), PROBE_INTERVAL_MS);
}

function stopProber(): void {
  if (probeTimer !== null) {
    clearInterval(probeTimer);
    probeTimer = null;
  }
}

function setReachable(next: boolean): void {
  if (next === reachable) {
    // No state change, but a successful probe while already reachable should stop the timer.
    if (next) stopProber();
    return;
  }
  reachable = next;
  // Drive @tanstack/query: offline → pause all queries (serve cache); online → resume.
  onlineManager.setOnline(next);
  if (next) stopProber();
  else startProber();
  emit();
}

async function runProbe(): Promise<void> {
  if (probing || probe === null) return;
  probing = true;
  try {
    if (await probe()) setReachable(true);
  } catch {
    /* a failed probe keeps us unreachable; the next tick retries */
  } finally {
    probing = false;
  }
}

export const connectivity = {
  /** React subscription (for `useSyncExternalStore`). */
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** Current reachability snapshot — `true` = reachable, `false` = web services unreachable. */
  getSnapshot(): boolean {
    return reachable;
  },
  /** Called by the REST client on any response from the backend (even a 4xx/5xx — it answered). */
  markReachable(): void {
    setReachable(true);
  },
  /** Called by the REST client when a `fetch` throws (the backend could not be reached). */
  markUnreachable(): void {
    setReachable(false);
  },
  /** Register the backend ping the reconnect prober uses — wired once by the REST client. */
  configureProbe(fn: Probe): void {
    probe = fn;
    if (!reachable) startProber();
  },
  /** Force an immediate reachability probe (window focus / online event). */
  probeNow(): void {
    void runProbe();
  },
};

// Take explicit control of onlineManager from the start — don't let it infer offline from
// `navigator.onLine` (wrong for the loopback desktop build).
onlineManager.setOnline(true);

// Window signals are HINTS only: they trigger a probe, whose real fetch against our backend is
// the source of truth. We never setReachable(false) from a window 'offline' event — loopback
// may still answer.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => connectivity.probeNow());
  window.addEventListener('offline', () => connectivity.probeNow());
  window.addEventListener('focus', () => {
    if (!reachable) connectivity.probeNow();
  });
}
